/**
 * Sticky add to cart bar - scroll to bundle selection
 *
 * When the product page renders a bundle / quantity discount selection (e.g. an app
 * block that offers "buy 2, save 30%" tiers), adding to cart straight from the sticky
 * bar would bypass that selection and add the plain product instead.
 *
 * On those products the sticky bar's add to cart button scrolls the customer up to the
 * bundle selection instead of submitting the form. When no bundle selection is present
 * the button keeps its default add to cart behaviour.
 *
 * The bundle selection is matched with the CSS selector set on the sticky bar's
 * `data-bundle-scroll-selector` attribute (theme editor setting).
 */
(() => {
  const selectors = {
    container: '.sticky-atc-bar[data-bundle-scroll-selector]',
    submit: '[name="add"]',
    stickyHeader: '.header-section.header-sticky',
  };
  const scrollMargin = 24;
  const scrollEndFallback = 700;
  const highlightClass = 'sticky-atc-bar-bundle-target';
  const highlightDuration = 1200;

  const isVisible = (element) => {
    const { display, visibility } = getComputedStyle(element);
    if (display === 'none' || visibility === 'hidden') return false;

    // A wrapper rendering its bundle through a shadow root can lay out as `display: contents`,
    // so it has no box of its own while still being on screen.
    return display === 'contents' || !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  };

  const getBundleTarget = (container) => {
    const selector = container.dataset.bundleScrollSelector;
    if (!selector) return null;

    let candidates = [];
    try {
      candidates = Array.from(document.querySelectorAll(selector));
    } catch (error) {
      // An invalid selector must not break the add to cart button.
      return null;
    }

    return candidates.find((element) => !element.closest('.sticky-atc-bar') && isVisible(element)) || null;
  };

  // The sticky header pins itself as soon as the page scrolls up, so its height has to be
  // reserved even while it is currently hidden - otherwise it lands on top of the bundle.
  const getHeaderOffset = () => {
    const headerSection = document.querySelector(selectors.stickyHeader);
    if (!headerSection) return 0;

    const header = headerSection.querySelector('header') || headerSection;
    return header.offsetHeight;
  };

  // An element laid out as `display: contents` has no box of its own, so measure its parent instead.
  const getMeasuredElement = (element) => {
    const { width, height } = element.getBoundingClientRect();
    if (width || height || !element.parentElement) return element;

    return getMeasuredElement(element.parentElement);
  };

  const onScrollEnd = (callback) => {
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', callback, { once: true });
      return;
    }

    setTimeout(callback, scrollEndFallback);
  };

  const alignBundle = (target) => {
    const top =
      window.scrollY + getMeasuredElement(target).getBoundingClientRect().top - getHeaderOffset() - scrollMargin;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  };

  const scrollToBundle = (target) => {
    alignBundle(target);

    // The header finishes pinning (and its navigation can expand again) while the page is still
    // scrolling, so correct the position once everything has settled.
    onScrollEnd(() => {
      const { top } = getMeasuredElement(target).getBoundingClientRect();
      if (top < getHeaderOffset() + scrollMargin - 1) alignBundle(target);
    });

    target.classList.add(highlightClass);
    setTimeout(() => target.classList.remove(highlightClass), highlightDuration);
  };

  const handle = (event, element) => {
    const container = element.closest(selectors.container);
    if (!container) return;

    const target = getBundleTarget(container);
    if (!target) return;

    event.preventDefault();
    event.stopPropagation();
    scrollToBundle(target);
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const submit = event.target.closest(selectors.submit);
      if (submit) handle(event, submit);
    },
    true
  );

  // Covers submitting the sticky bar form with the keyboard (e.g. Enter in the quantity input).
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof Element) handle(event, event.target);
    },
    true
  );
})();
